import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/solution-keys";

export interface SolutionListRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  versionCount: number;
  currentVersionId: string | null;
  currentVersionNumber: number | null;
  unresolvedCommentCount: number;
}

export interface SolutionDetail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  currentVersion: VersionDetail | null;
}

export interface VersionDetail {
  id: string;
  solutionId: string;
  versionNumber: number;
  label: string | null;
  sourceMd: string;
  parsedData: unknown;
  isCurrent: boolean;
  uploadedBy: string;
  uploadedByName?: string;
  commitSha: string | null;
  changeSummary: string | null;
  createdAt: string;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function useSolutions() {
  return useQuery({
    queryKey: solutionKeys.list(),
    queryFn: async (): Promise<SolutionListRow[]> => {
      const json = await api<{ data: SolutionListRow[] }>("/api/solutions");
      return json.data;
    },
  });
}

export function useSolution(slug: string) {
  return useQuery({
    queryKey: solutionKeys.detail(slug),
    enabled: Boolean(slug),
    queryFn: async (): Promise<SolutionDetail> => {
      const json = await api<{ data: SolutionDetail }>(`/api/solutions/${slug}`);
      return json.data;
    },
  });
}

export function useCreateSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; slug?: string }) => {
      const json = await api<{ data: SolutionListRow }>("/api/solutions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.all });
      toast.success("Solution created");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to create solution");
    },
  });
}

export function useUpdateSolution(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name?: string; description?: string; slug?: string }) => {
      const json = await api<{ data: SolutionListRow }>(`/api/solutions/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.all });
      toast.success("Solution updated");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update solution");
    },
  });
}

export function useDeleteSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      await api<{ success: true }>(`/api/solutions/${slug}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.all });
      toast.success("Solution deleted");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to delete solution");
    },
  });
}
